// © 2026 AttainIP Engineering
/**
 * The global error sink (RFC-0202 §"Session-Loss UX" §2).
 *
 * Every query/mutation error the QueryClient sees arrives here. Anything a
 * component does NOT render itself becomes an ErrorBanner — so a failed poll
 * on the analysis page cannot silently freeze the UI.
 *
 * It lives in its own module rather than inside `main.tsx` so the decision it
 * makes — banner, or not — is testable without booting the app.
 *
 * FOUR REASONS NOT TO RAISE THE BANNER, and they are the only four:
 *  1. the request was cancelled (not a failure at all),
 *  2. the session is gone (the login redirect owns that),
 *  3. the call declared that its consumer renders its own errors
 *     (`store/handledErrors`),
 *  4. — there is no fourth. Everything else raises it. That is the point:
 *     silence is the one outcome worse than the wrong message.
 */

import { ApiRequestError } from '../api/client';
import { useErrorBannerStore } from './errorBanner';
import { declaresHandledErrors } from './handledErrors';

/** The Query or Mutation the error came from; only its `meta` is read. */
export interface ErrorSource {
  meta?: Record<string, unknown>;
}

export function reportUnhandledError(error: unknown, source?: ErrorSource): void {
  // A cancelled/aborted request is NOT a failure — e.g. a poll superseded when
  // the user reverts a version. It must never surface as an error banner.
  if (error instanceof DOMException && error.name === 'AbortError') return;
  if (error instanceof Error && error.name === 'CancelledError') return;
  // Session loss is handled by the login redirect, not the banner.
  if (error instanceof ApiRequestError && error.code === 'SESSION_EXPIRED') {
    return;
  }
  // The consumer already answered this error in its own words. Raising the
  // banner too would contradict it — see `store/handledErrors`.
  if (declaresHandledErrors(source?.meta)) return;
  useErrorBannerStore.getState().setError(error);
}
